import { supabase } from '../../lib/supabase'
import { toPence } from '../../utils/currency'
import { getCurrentUserId, verifyFinancialWriteAccess } from '../../lib/auth'
import { applyPropertyFilter } from '../../lib/portfolioFilter'
import { validateFile, sanitizeFilename } from '../../lib/validators'
import { resolveExpenseReceiptUrls } from './helpers'
import type {
  Expense,
  ExpenseInsert,
  ExpenseUpdate,
  TransactionCategory,
  TransactionSource,
  TransactionType,
} from '../../types/financial'

/** Optional overrides for the auto-linked financial transaction */
export interface ExpenseTxOverrides {
  contractor_id?: string
  source?: TransactionSource
  source_ref?: string
  supplier_name?: string
}

function computeTaxYear(dateStr: string): string {
  const d = new Date(dateStr)
  const year = d.getFullYear()
  const month = d.getMonth()
  const day = d.getDate()
  const taxYearStart = (month < 3 || (month === 3 && day < 6)) ? year - 1 : year
  return `${taxYearStart}-${(taxYearStart + 1).toString().slice(-2)}`
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapExpenseRow(row: any, fallbackUserId?: string): Expense {
  return {
    id: row.id,
    property_id: row.property_id,
    tenancy_id: row.tenancy_id || null,
    category: row.category as TransactionCategory,
    custom_category_id: null,
    description: row.description,
    amount: row.amount_total_pence / 100,
    expense_date: row.transaction_date,
    receipt_storage_path: row.receipt_storage_path,
    receipt_bucket: row.receipt_bucket,
    receipt_url: null,
    receipt_name: null,
    tax_year: computeTaxYear(row.transaction_date),
    is_deductible: row.is_tax_deductible ?? true,
    linked_issue_id: row.issue_id || null,
    created_by: row.audit_created_by || fallbackUserId,
    created_at: row.created_at,
    updated_at: row.updated_at,
    property: row.property,
  }
}

export async function getExpenses(propertyId?: string, taxYear?: string, propertyIds?: string[], userId?: string): Promise<Expense[]> {
  let query = supabase
    .from('financial_transactions')
    .select(`
      id, property_id, tenancy_id, category, description,
      amount_total_pence, transaction_date, receipt_storage_path, receipt_bucket,
      is_tax_deductible, issue_id, audit_created_by,
      created_at, updated_at,
      property:properties!inner(id, address_line1, city)
    `)
    .in('type', ['expense', 'mortgage_interest'])
    .eq('status', 'confirmed')
    .is('deleted_at', null)
    .order('transaction_date', { ascending: false })

  query = applyPropertyFilter(query, propertyIds)
  if (propertyId) {
    query = query.eq('property_id', propertyId)
  }

  if (taxYear) {
    const [startYear] = taxYear.split('-').map(Number)
    query = query
      .gte('transaction_date', `${startYear}-04-06`)
      .lte('transaction_date', `${startYear + 1}-04-05`)
  }

  const { data, error } = await query
  if (error) throw error

  const expenses: Expense[] = (data || []).map((row) => mapExpenseRow(row, userId))

  return await resolveExpenseReceiptUrls(expenses)
}

export async function createExpense(input: ExpenseInsert, txOverrides?: ExpenseTxOverrides): Promise<Expense> {
  const userId = await getCurrentUserId()

  // Resolve owner_id from the property's owner
  const { data: property, error: propError } = await supabase
    .from('properties')
    .select('owner_id')
    .eq('id', input.property_id)
    .single()

  if (propError) throw propError
  const ownerId = property.owner_id || userId

  const amountPence = toPence(input.amount)
  const transactionCategory = input.category

  // Section 24: mortgage_interest is a separate type, NOT an allowable expense
  const isMortgageInterest = input.category === 'mortgage_interest'
  const txType: TransactionType = isMortgageInterest ? 'mortgage_interest' : 'expense'

  // Write to financial_transactions as the single source of truth
  const { data, error } = await supabase
    .from('financial_transactions')
    .insert({
      owner_id: ownerId,
      property_id: input.property_id,
      tenancy_id: input.tenancy_id || null,
      issue_id: input.linked_issue_id || null,
      type: txType,
      category: transactionCategory,
      description: input.description,
      amount_net_pence: amountPence,
      amount_vat_pence: 0,
      amount_total_pence: amountPence,
      vat_rate: 0,
      transaction_date: input.expense_date,
      receipt_storage_path: input.receipt_storage_path || null,
      receipt_bucket: input.receipt_bucket || null,
      receipt_filename: input.receipt_name || null,
      is_tax_deductible: isMortgageInterest ? false : (input.is_deductible ?? true),
      audit_created_by: userId,
      source: txOverrides?.source ?? 'manual',
      source_ref: txOverrides?.source_ref ?? null,
      contractor_id: txOverrides?.contractor_id ?? null,
      supplier_name: txOverrides?.supplier_name ?? null,
    })
    .select(`
      id, property_id, tenancy_id, category, description,
      amount_total_pence, transaction_date, receipt_storage_path, receipt_bucket,
      is_tax_deductible, issue_id, audit_created_by,
      created_at, updated_at,
      property:properties!inner(id, address_line1, city)
    `)
    .single()

  if (error) throw error

  // Map to Expense shape (same mapping as getExpenses)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const row = data as any
  const expense: Expense = {
    id: row.id,
    property_id: row.property_id,
    tenancy_id: row.tenancy_id || null,
    category: row.category as TransactionCategory,
    custom_category_id: null,
    description: row.description,
    amount: row.amount_total_pence / 100,
    expense_date: row.transaction_date,
    receipt_storage_path: row.receipt_storage_path,
    receipt_bucket: row.receipt_bucket,
    receipt_url: null,
    receipt_name: null,
    tax_year: computeTaxYear(row.transaction_date),
    is_deductible: row.is_tax_deductible ?? true,
    linked_issue_id: row.issue_id || null,
    created_by: row.audit_created_by || userId,
    created_at: row.created_at,
    updated_at: row.updated_at,
    property: row.property,
  }

  // Audit trail
  try {
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'expense_created',
      entity_type: 'expense',
      entity_id: expense.id,
      new_data: { amount: input.amount, category: input.category, property_id: input.property_id, description: input.description },
    })
  } catch {
    // Non-blocking
  }

  return expense
}

export async function updateExpense(id: string, input: ExpenseUpdate): Promise<Expense> {
  const userId = await getCurrentUserId()

  // Build the update payload, translating Expense field names to financial_transactions columns
  const updates: Record<string, unknown> = {}

  if (input.description !== undefined) updates.description = input.description
  if (input.amount !== undefined) {
    const amountPence = toPence(input.amount)
    updates.amount_net_pence = amountPence
    updates.amount_total_pence = amountPence
  }
  if (input.expense_date !== undefined) updates.transaction_date = input.expense_date
  if (input.receipt_storage_path !== undefined) updates.receipt_storage_path = input.receipt_storage_path
  if (input.receipt_bucket !== undefined) updates.receipt_bucket = input.receipt_bucket
  if (input.receipt_name !== undefined) updates.receipt_filename = input.receipt_name
  if (input.is_deductible !== undefined) updates.is_tax_deductible = input.is_deductible
  if (input.category !== undefined) {
    updates.category = input.category
  }

  // Reset xero_sync_status so the change gets re-synced
  const { data: existing, error: fetchError } = await supabase
    .from('financial_transactions')
    .select('xero_sync_status')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (fetchError) throw fetchError

  if (existing?.xero_sync_status === 'synced') {
    updates.xero_sync_status = 'pending'
  }

  // Update financial_transactions directly (RLS enforces owner/team access)
  const { data, error } = await supabase
    .from('financial_transactions')
    .update(updates)
    .eq('id', id)
    .select(`
      id, property_id, tenancy_id, category, description,
      amount_total_pence, transaction_date, receipt_storage_path, receipt_bucket,
      is_tax_deductible, issue_id, audit_created_by,
      created_at, updated_at,
      property:properties!inner(id, address_line1, city)
    `)
    .single()

  if (error) throw error

  // Map to Expense shape (same mapping as getExpenses)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const row = data as any
  const expense: Expense = {
    id: row.id,
    property_id: row.property_id,
    tenancy_id: row.tenancy_id || null,
    category: row.category as TransactionCategory,
    custom_category_id: null,
    description: row.description,
    amount: row.amount_total_pence / 100,
    expense_date: row.transaction_date,
    receipt_storage_path: row.receipt_storage_path,
    receipt_bucket: row.receipt_bucket,
    receipt_url: null,
    receipt_name: null,
    tax_year: computeTaxYear(row.transaction_date),
    is_deductible: row.is_tax_deductible ?? true,
    linked_issue_id: row.issue_id || null,
    created_by: row.audit_created_by || userId,
    created_at: row.created_at,
    updated_at: row.updated_at,
    property: row.property,
  }

  // Audit trail
  try {
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'expense_updated',
      entity_type: 'expense',
      entity_id: id,
      new_data: input,
    })
  } catch { /* Non-blocking */ }

  return expense
}

export async function deleteExpense(id: string): Promise<void> {
  const userId = await getCurrentUserId()

  // Fetch the transaction for property_id (RLS enforces read access)
  const { data: transaction } = await supabase
    .from('financial_transactions')
    .select('property_id, owner_id')
    .eq('id', id)
    .is('deleted_at', null)
    .single()

  if (!transaction) throw new Error('Expense not found')

  // soft_delete_record is SECURITY DEFINER — verify financial write access before calling
  await verifyFinancialWriteAccess(transaction.property_id)

  // Soft-delete from financial_transactions by primary key
  const { error } = await supabase.rpc('soft_delete_record', {
    target_table: 'financial_transactions',
    record_id: id,
  })

  if (error) throw error

  // Audit trail
  try {
    await supabase.from('audit_log').insert({
      user_id: userId,
      action: 'expense_deleted',
      entity_type: 'expense',
      entity_id: id,
      old_data: { id },
    })
  } catch {
    // Non-blocking
  }

}

export async function uploadReceipt(expenseId: string, file: File): Promise<Expense> {
  // Validate file before upload
  const validation = validateFile(file)
  if (!validation.valid) {
    throw new Error(validation.error)
  }

  // Upload file
  const sanitizedName = sanitizeFilename(file.name)
  const fileExt = sanitizedName.split('.').pop()
  const fileName = `${expenseId}/${Date.now()}.${fileExt}`

  const { error: uploadError } = await supabase.storage
    .from('expense-receipts')
    .upload(fileName, file)

  if (uploadError) throw uploadError

  // Store the storage path (not a public URL) — signed URLs are generated at
  // read time. The bucket is recorded as a value rather than smuggled into the
  // path as a prefix (migration 480).
  return updateExpense(expenseId, {
    receipt_storage_path: fileName,
    receipt_bucket: 'expense-receipts',
    receipt_name: sanitizedName
  })
}
